import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import NotificationContext from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';

const NotificationOffcanvas = () => {
  const { showPanel, closePanel, fetchUnreadCount } = useContext(NotificationContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showConfirm } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (showPanel) {
      fetchNotifications();
    }
  }, [showPanel]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 읽은 알림은 사흘 뒤 서버가 알아서 지운다. 이 버튼은 지금 당장 비우고
  // 싶을 때를 위한 것이다. 되돌릴 수 없으니 한 번 묻는다.
  const handleDeleteAll = async () => {
    const ok = await showConfirm(
      '알림 비우기',
      '알림을 모두 지울까요? 점검 기록과 이슈는 그대로 남습니다.',
      'danger',
    );
    if (!ok) return;

    try {
      await api.delete('/notifications');
      setNotifications([]);
      fetchUnreadCount();
    } catch (e) {
      console.error(e);
    }
  };

  const handleRead = async (notif) => {
    if (!notif.isRead) {
      try {
        await api.post(`/notifications/${notif.id}/read`);
        fetchUnreadCount();
        setNotifications(notifications.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
      } catch (e) {
        console.error(e);
      }
    }

    closePanel();

    // 갈 곳은 알림을 만든 쪽이 정해서 적어 둔다. 여기서 type 으로 되짚던 때에는
    // '13곳을 마쳤습니다' 를 눌러도 그중 한 곳의 점검표만 열렸다.
    if (notif.path) {
      navigate(notif.path);
      return;
    }

    // path 가 없는 옛 알림. 지금까지 하던 대로 보낸다.
    if (notif.type === 'ISSUE' && notif.targetId) {
      navigate(`/issues/${notif.targetId}`);
    } else if (notif.type === 'INSPECTION' && notif.targetId) {
      navigate(`/reports/${notif.targetId}`);
    }
  };

  const handleReadAll = async () => {
    try {
      await api.post('/notifications/read-all');
      fetchUnreadCount();
      setNotifications(notifications.map(n => ({...n, isRead: true})));
    } catch (e) {}
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // 누를 때 상세페이지로 이동하지 않도록 방지
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      fetchUnreadCount(); // 지우면 안 읽은 개수도 변동될 수 있으므로
    } catch (e) {
      console.error(e);
    }
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return `방금 전`;
    if (diff < 3600) return `${Math.floor(diff/60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff/3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff/86400)}일 전`;
    return d.toLocaleDateString();
  };

  return (
    <>
      {/* 안드로이드에서는 이 판이 상태바 밑까지 올라온다. 시각과 배터리 위에
          '알림 센터' 가 겹쳐 찍혀서, 위쪽 여백을 상태바 높이만큼 준다.
          App.js 가 capacitor-plugin-safe-area 로 실제 값을 채운다. */}
      <div
        className={`offcanvas offcanvas-end shadow ${showPanel ? 'show' : ''}`}
        tabIndex="-1"
        style={{
          visibility: showPanel ? 'visible' : 'hidden',
          zIndex: 1050,
          paddingTop: 'var(--safe-area-inset-top)',
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title fw-bold">알림 센터 <i className="bi bi-bell-fill text-warning ms-1"></i></h5>
          <button type="button" className="btn-close" onClick={closePanel}></button>
        </div>
        <div className="offcanvas-body p-0 bg-light">
          <div className="p-3 bg-white d-flex justify-content-between align-items-center border-bottom">
            <span className="small text-muted fw-bold">최근 알림</span>
            {/* 크기를 직접 지정한다. 부트스트랩의 .btn 이 font-size: 1rem 을 걸고
                그게 .small 보다 뒤에 선언돼 있어서, small 만으로는 왼쪽 '최근 알림'
                보다 커진다.
                문구도 갈랐다. '모두 읽음'과 '모두 지우기'는 형태가 너무 비슷한데
                하나는 되돌릴 수 없는 동작이라, 나란히 두면 잘못 누르기 쉽다.
                아이콘까지 다르면 손이 먼저 알아본다. */}
            <span className="d-flex align-items-center gap-3">
              <button
                className="btn btn-link p-0 fw-bold text-decoration-none text-primary d-flex align-items-center gap-1"
                style={{ fontSize: '0.875rem' }}
                onClick={handleReadAll}
              >
                <i className="bi bi-check2-all"></i>모두 읽음
              </button>
              <button
                className="btn btn-link p-0 fw-bold text-decoration-none text-danger d-flex align-items-center gap-1"
                style={{ fontSize: '0.875rem' }}
                onClick={handleDeleteAll}
              >
                <i className="bi bi-trash3"></i>비우기
              </button>
            </span>
          </div>
          
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center p-5 mt-5 text-muted">
              <i className="bi bi-bell-slash fs-1 mb-3 d-block opacity-25"></i>
              <p className="mb-0 fw-bold">새로운 알림이 없습니다.</p>
              <small>알림이 도착하면 여기에 표시됩니다.</small>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {notifications.map(notif => (
                <div 
                  key={notif.id} 
                  className={`list-group-item list-group-item-action p-3 border-bottom position-relative ${notif.isRead ? 'bg-white text-muted' : 'bg-primary-subtle'}`}
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                  onClick={() => handleRead(notif)}
                >
                  <div className="d-flex w-100 justify-content-between mb-2">
                    <h6 className={`mb-0 fw-bold d-flex align-items-center ${notif.isRead ? 'opacity-75' : ''}`} style={{ fontSize: '0.95rem', paddingRight: '20px' }}>
                      {notif.type === 'ISSUE' ? <span className="badge bg-danger me-2">긴급</span>
                       : notif.type === 'INSPECTION' ? <span className="badge bg-success me-2">점검</span>
                       : notif.type === 'LEASE' ? <span className="badge bg-primary me-2">임대차</span>
                       : <span className="badge bg-secondary me-2">시스템</span>}
                      {notif.title}
                    </h6>
                    <button 
                      className="btn btn-sm p-0 border-0 text-muted" 
                      onClick={(e) => handleDelete(e, notif.id)}
                      title="알림 삭제"
                      style={{ position: 'absolute', right: '1rem', top: '1rem', transition: 'color 0.2s', backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#dc3545'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                  <p className={`mb-1 small ${notif.isRead ? 'opacity-75' : ''}`}>{notif.message}</p>
                  <div className="text-end">
                     <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                       {timeAgo(notif.createdAt)}
                     </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showPanel && <div className="offcanvas-backdrop fade show" onClick={closePanel} style={{ zIndex: 1040 }}></div>}
    </>
  );
};

export default NotificationOffcanvas;
